import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Text from '../../../components/Text.js';
import * as Services from '../../../services/index.js';
import * as dates from '@simplybenefits/sb-dates';
import { helpers } from '@simplybenefits/sb-utils';
import DivisionListItem from './components/DivisionListItem.js';
import i18n from '../../../localization/i18n.js';
import Overview from './Overview.js';
import tokens from '../../../localization/tokens.js';
import Loader from '../../../components/Loader.js';

/**
 * 
 * @returns 
 */
const Index = ({
  isCurrent,
  invoice,
  downloadInvoice,
  account,
  hideOverview,
  divisions,
  whitelabel,
  isMasterSite,
  loader,
  accountClasses,
  triggerDivisionRepayment
}) => {
  const [state, setState] = useState({
    divisionIDs: helpers.sortObjByTitle(divisions),
    selectedItem: 'overview',
    currentDivision: null,
    loadIndex: 10,
    query: '',
    selectedDivisionID: null,
    invoice: null
  });

  /**
   * 
   * @param {*} selectedItem 
   * @param {*} divisionID 
   */
  const _selectDivision = (selectedItem, divisionID) => {
    setState(prevState => ({
      ...prevState
    }));
    if (selectedItem === "overview") {
      return setState(prevState => ({
        ...prevState,
        selectedItem: "overview",
        selectedDivisionID: null,
        currentDivision: null
      }));
    }
    const currentDivision = !divisionID ? null : invoice.currentTransactions.divisions[divisionID];
    let divisionSummary = false;
    if (currentDivision) {
      // Find the summary for the division
      if (state.invoice.summary.divisionSummaries) {
        divisionSummary = invoice.summary.divisionSummaries.find(divSum => divSum.meta.divisionID === divisionID);

        // let's use the more complete totals from the current transactions division instead of the division summary as it's missing data we need
        divisionSummary.totals = currentDivision.totals;
        setState(prevState => ({
          ...prevState,
          selectedItem,
          divisionSummary,
          currentDivision: currentDivision,
          selectedDivisionID: divisionID
        }));
      }
    }
    return null;
  };

  // useEffect to set the invoice in the state when the component mounts or when the invoice prop changes
  useEffect(() => {
    // We need to strip the account invoice of member paid premiums and adjustments for display purposes
    const invoiceForDisplay = helpers.removeVoluntaryAdjustmentsAndPremiums(invoice);
    setState(prevState => ({
      ...prevState,
      invoice: invoiceForDisplay
    }));
  }, [invoice]);

  // useEffect to handle selecting the division after the invoice state is set
  useEffect(() => {
    if (state.invoice && hideOverview) {
      // If the overview should be hidden, get the top division and show it as default
      const topDivision = divisions[state.divisionIDs[0]];
      _selectDivision(topDivision.title, topDivision.divisionID);
    }
  }, [state.invoice]);
  if (!state.invoice) {
    return /*#__PURE__*/React.createElement(Loader, {
      whitelabel: whitelabel
    });
  }
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    alignItems: "center"
  }, isCurrent ? /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4,
    sx: {
      paddingTop: "0px",
      paddingBottom: "0px",
      paddingLeft: "0px",
      paddingRight: "8px"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    align: "center",
    mediumLarge: true,
    display: "inline"
  }, i18n.t(tokens.labels.invoices.currentAllocation))) : /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4,
    sx: {
      padding: "8px"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    align: "center",
    mediumLarge: true,
    display: "inline"
  }, dates.getLocalDisplay(invoice?.meta?.dateAdded, 'MMMM YYYY', i18n.locale)))), /*#__PURE__*/React.createElement(Grid, {
    container: true
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 2,
    md: 3,
    sx: {
      paddingTop: "8px",
      paddingBottom: "8px",
      paddingLeft: "0px",
      paddingRight: "12px"
    }
  }, !hideOverview && /*#__PURE__*/React.createElement(DivisionListItem, {
    data: {
      title: i18n.t(tokens.labels.invoices.overview)
    },
    onSelect: () => _selectDivision('overview'),
    selected: state.selectedItem === 'overview'
  }), helpers.sortObjByTitle(divisions).map((divisionID, i) => {
    let data = invoice.currentTransactions.divisions[divisionID];

    // if this division did not exist at time of the invoice generation then don't show the label
    if (!data) {
      return null;
    }
    const title = divisions[divisionID].title;
    const description = divisions[divisionID].description;
    return /*#__PURE__*/React.createElement(DivisionListItem, {
      id: i === 0 ? 'invoices-division' : `invoices-division-${i}`,
      key: Services.concatTokens(divisionID, divisions[divisionID].title),
      data: {
        title,
        description
      },
      onSelect: () => _selectDivision(title, divisionID),
      selected: state.selectedItem === title
    });
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 10,
    md: 9,
    sx: {
      paddingTop: "8px",
      paddingBottom: "8px",
      paddingLeft: "4px",
      paddingRight: "0px"
    }
  }, state.currentDivision ? /*#__PURE__*/React.createElement(Overview, {
    invoice: state.invoice,
    overallSummary: state.divisionSummary,
    account: account,
    isCurrent: isCurrent,
    divisionTitle: state.currentDivision.title,
    whitelabel: whitelabel,
    loader: loader,
    isDivisionSummary: true,
    selectedDivisionID: state.selectedDivisionID,
    accountClasses: accountClasses,
    divisions: divisions,
    hideOverview: hideOverview,
    isMasterSite: isMasterSite,
    triggerDivisionRepayment: () => triggerDivisionRepayment(state.selectedDivisionID, invoice.invoiceNum),
    downloadInvoice: downloadInvoice,
    users: Object.values(state.invoice.currentTransactions.users).filter(user => user.meta.divisionID === state.selectedDivisionID)
  }) : /*#__PURE__*/React.createElement(Overview, {
    invoice: state.invoice,
    overallSummary: invoice.summary.overallSummary,
    account: account,
    isCurrent: isCurrent,
    whitelabel: whitelabel,
    loader: loader,
    isDivisionSummary: false,
    accountClasses: accountClasses,
    divisions: divisions,
    hideOverview: hideOverview,
    isMasterSite: isMasterSite,
    downloadInvoice: downloadInvoice,
    users: Object.values(state.invoice.currentTransactions.users)
  }))), /*#__PURE__*/React.createElement("span", {
    id: "invoices-overview"
  }));
};
export default Index;